import { useMemo, useState } from "react";
import { CalendarDays, ChevronRight, Flame, MapPin } from "lucide-react";
import { useCustom, useGetLocale, useTranslate } from "@refinedev/core";
import { StatusBadge, type StatusTone } from "@fawkes/ui";

import { formatDate, formatEuro } from "../i18n/format";

// De tegel Crematies op het klantscherm, zoals de mockup hem tekent: drie cijfers bovenaan (lopend,
// totaal, laatste crematie), twee filters, en daaronder per uitvaart een kaart — het dier met zijn
// soort en status, de vestiging en het dossiernummer, de vorm en waar het dier vandaan kwam, de
// data en het bedrag met de factuur. Een rij in een tabel zegt hetzelfde, maar leest niet als een
// uitvaart; deze kaarten zijn dezelfde vorm als de verblijven bij het asiel.
//
// De url is RELATIEF ("crematorium/relaties/…") — de dataProvider zet er één keer /api voor.

interface KlantDossier {
  id: string;
  dossierNummer: string | null;
  dierNaam: string | null;
  diersoort: string | null;
  verwerkingsvorm: string | null;
  status: string | null;
  aanmeldDatum: string | null;
  planningDatum: string | null;
  totaalBedrag: number | null;
  betaalStatus: string | null;
  vestigingNaam: string | null;
  herkomst: string | null;
  factuurNummer: string | null;
}

const STATUS_TONE: Record<string, StatusTone> = {
  AFGEROND: "success",
  GECREMEERD: "success",
  GEANNULEERD: "danger",
  WACHT_OP_BETALING: "warning",
};

/** Afgerond of geannuleerd is klaar; al het andere loopt nog. */
const LOOPT_NOG = (status: string | null) => status !== "AFGEROND" && status !== "GEANNULEERD";

function diersoortEmoji(diersoort: string | null): string {
  const s = (diersoort ?? "").toLowerCase();
  if (s.includes("kat")) return "🐱";
  if (s.includes("hond")) return "🐶";
  if (s.includes("konijn")) return "🐰";
  if (s.includes("vogel")) return "🐦";
  return "🐾";
}

export function CrematiesTile({
  relatieId,
  onOpenDossier,
}: {
  relatieId: string;
  onOpenDossier?: (dossierId: string) => void;
}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const [vestigingFilter, setVestigingFilter] = useState("");
  const [periodeFilter, setPeriodeFilter] = useState<"alles" | "lopend" | "afgerond">("alles");

  const { data, isLoading, isError } = useCustom<KlantDossier[]>({
    url: `crematorium/relaties/${relatieId}/dossiers`,
    method: "get",
  });
  const rijen = useMemo(() => (Array.isArray(data?.data) ? data.data : []), [data]);

  const vestigingen = useMemo(
    () => [...new Set(rijen.map((r) => r.vestigingNaam).filter((n): n is string => !!n))],
    [rijen],
  );
  const lopend = rijen.filter((r) => LOOPT_NOG(r.status)).length;
  // De laatste crematie: de nieuwste datum waarop er daadwerkelijk iets gepland stond.
  const laatste = rijen
    .map((r) => r.planningDatum ?? r.aanmeldDatum)
    .filter((d): d is string => !!d)
    .sort()
    .at(-1);

  const zichtbaar = rijen.filter(
    (r) =>
      (vestigingFilter === "" || r.vestigingNaam === vestigingFilter)
      && (periodeFilter === "alles"
        || (periodeFilter === "lopend" ? LOOPT_NOG(r.status) : !LOOPT_NOG(r.status))),
  );

  if (isError) {
    return <p className="fawkes-tile-placeholder">{t("crematiesTile.error")}</p>;
  }

  const open = (id: string) => onOpenDossier?.(id);

  return (
    <div className="fawkes-relatie-tile fawkes-relatie-tile--fill">
      <div className="fawkes-relatie-facturen__totals fawkes-relatie-verblijf__totalen">
        <div className="fawkes-relatie-facturen__total">
          <span className="fawkes-relatie-facturen__total-label">{t("crematiesTile.lopend")}</span>
          <span className="fawkes-relatie-verblijf__actief">
            <span className="fawkes-relatie-facturen__total-value">{lopend}</span>
            {lopend > 0 && <StatusBadge tone="warning">{t("crematiesTile.inBehandeling")}</StatusBadge>}
          </span>
        </div>
        <div className="fawkes-relatie-facturen__total">
          <span className="fawkes-relatie-facturen__total-label">{t("crematiesTile.totaal")}</span>
          <span className="fawkes-relatie-facturen__total-value">{rijen.length}</span>
        </div>
        <div className="fawkes-relatie-facturen__total">
          <span className="fawkes-relatie-facturen__total-label">{t("crematiesTile.laatste")}</span>
          <span className="fawkes-relatie-facturen__total-value">{laatste ? formatDate(laatste, locale) : "—"}</span>
        </div>
      </div>

      <div className="fawkes-relatie-verblijf__filters">
        <span className="fawkes-relatie-filterpil">
          <MapPin size={16} aria-hidden="true" />
          <select
            aria-label={t("crematiesTile.filter.vestiging")}
            value={vestigingFilter}
            onChange={(e) => setVestigingFilter(e.target.value)}
          >
            <option value="">{t("crematiesTile.filter.alleVestigingen")}</option>
            {vestigingen.map((v) => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
        </span>
        <span className="fawkes-relatie-filterpil">
          <CalendarDays size={16} aria-hidden="true" />
          <select
            aria-label={t("crematiesTile.filter.periode")}
            value={periodeFilter}
            onChange={(e) => setPeriodeFilter(e.target.value as "alles" | "lopend" | "afgerond")}
          >
            <option value="alles">{`${t("crematiesTile.filter.periode")}: ${t("crematiesTile.filter.alles")}`}</option>
            <option value="lopend">{t("crematiesTile.inBehandeling")}</option>
            <option value="afgerond">{t("crematies.status.AFGEROND")}</option>
          </select>
        </span>
      </div>

      <ul className="fawkes-relatie-verblijf__cards">
        {zichtbaar.map((r) => (
          <li
            key={r.id}
            className={`fawkes-relatie-verblijf__card${onOpenDossier ? " is-klikbaar" : ""}`}
            role={onOpenDossier ? "button" : undefined}
            tabIndex={onOpenDossier ? 0 : undefined}
            onClick={onOpenDossier ? () => open(r.id) : undefined}
            onKeyDown={
              onOpenDossier
                ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      open(r.id);
                    }
                  }
                : undefined
            }
          >
            <span className="fawkes-relatie-verblijf__thumb">
              <span aria-hidden="true">{diersoortEmoji(r.diersoort)}</span>
            </span>
            <div className="fawkes-relatie-verblijf__body">
              <span className="fawkes-relatie-verblijf__title">
                <span className="fawkes-relatie-verblijf__naam">
                  {r.dierNaam ?? t("crematoriumWerklijst.dier.onbekend")}
                </span>
                {r.diersoort && <StatusBadge tone="neutral">{r.diersoort}</StatusBadge>}
                {r.status && (
                  <StatusBadge tone={STATUS_TONE[r.status] ?? "warning"}>
                    {t(`crematies.status.${r.status}`)}
                  </StatusBadge>
                )}
              </span>
              <span className="fawkes-relatie-verblijf__line">
                {[r.vestigingNaam, r.dossierNummer].filter(Boolean).join(" · ") || "—"}
              </span>
              <span className="fawkes-relatie-verblijf__line">
                {[
                  r.verwerkingsvorm ? t(`crematoriumTarief.vorm.${r.verwerkingsvorm}`) : null,
                  r.herkomst ? t(`crematies.herkomst.${r.herkomst}`) : null,
                ].filter(Boolean).join(" · ") || "—"}
              </span>
              <span className="fawkes-relatie-verblijf__streep" aria-hidden="true" />
              <span className="fawkes-relatie-verblijf__dates">
                <CalendarDays size={14} aria-hidden="true" />
                {formatDate(r.aanmeldDatum, locale)}
                <span aria-hidden="true">→</span>
                <CalendarDays size={14} aria-hidden="true" />
                {r.planningDatum ? formatDate(r.planningDatum, locale) : t("crematiesTile.nogTePlannen")}
              </span>
              <span className="fawkes-relatie-verblijf__duur">
                {[
                  r.totaalBedrag != null ? formatEuro(r.totaalBedrag, locale) : null,
                  r.factuurNummer
                    ? `${r.factuurNummer}${r.betaalStatus ? ` ${t(`crematies.betaal.${r.betaalStatus}`).toLowerCase()}` : ""}`
                    : t("crematiesTile.geenFactuur"),
                ].filter(Boolean).join(" · ")}
              </span>
            </div>
            <ChevronRight size={16} aria-hidden="true" className="fawkes-relatie-row-chevron" />
          </li>
        ))}
      </ul>

      {zichtbaar.length === 0 && (
        <p className="fawkes-tile-placeholder">
          {isLoading ? t("common.loading") : rijen.length === 0 ? t("crematiesTile.empty") : t("crematiesTile.filter.geen")}
        </p>
      )}

      <div className="fawkes-relatie-tegelvoet">
        <span className="fawkes-relatie-alleslink" aria-hidden="true">
          <Flame size={15} />
        </span>
        <span className="fawkes-relatie-tegelvoet__rest">
          {t("crematiesTile.aantal", { args: [rijen.length] })}
        </span>
      </div>
    </div>
  );
}
